/**
 * Общие части и некоторые отдельные функции для работы плеера.
 */

import FlussonicMsePlayer from "@flussonic/flussonic-mse-player";
import Hls from "hls.js";
import Vue from "vue";
import {VideoPlayerWithWebSocket} from "./msePlayer.js";

/**
 * Массив с параметрами для размеров временной шкалы.
 * - количество секунд на пиксель,
 * - размер отступов между засечками
 *
 * Размеры следует менять не пропорционально, иначе визуально не будет заметно что есть изменения.
 */
export const TIMELINE_SIZES = [
  [1, 60],
  [5, 55],
  [10, 50],
  [30, 45],
  [60, 40],
  [180, 35],
  [300, 30],
  [500, 25],
  [700, 20],
  [1000, 15],
];

// Ниже ряд абстракций для проброса вызовов внутрь библиотек, которые обеспечивают воспроизведение видео.

/**
 * Абстракция поверх FlussonicMsePlayer.
 *
 * В отличие от hls.js начальная настройка номера воспроизводимого потока осуществляется непосредственно после загрузки метаданных
 * и после получения сигнала от по WebSocket о запуске трансляции.
 *
 * Непосредственно до начала трансляции поменять поток нельзя.
 * onMediaInfo вызывается каждый раз при изменении потока, а актуальный поток там не отслеживается (баг),
 * кроме того если в потоке есть аудио то из-за магии асинхронности в js накладываются методы которые несовместимы и трансляция сбрасывается.
 *
 * Поэтому опытным путем для актуальной версии было определено что возможно менять поток только специальным методом и по событию onResume.
 */
export class PlayerMSE {
  /**
   * @param {HTMLVideoElement|Element} elementPlayer
   * @param {String} sourceUrl
   * @param {Function} onError
   */
  constructor(elementPlayer, sourceUrl, onError) {
    this.elementPlayer = elementPlayer;
    this.instancePlayer = new FlussonicMsePlayer(elementPlayer, sourceUrl, {
      onError(error) {
        devLog("[FlussonicMsePlayer error]", error);
        // Не все ошибки нужно обрабатывать.
        if (error.error !== "play_promise_reject") {
          onError();
        }
        // todo не возникает ошибка при запросе несуществующего номера потока.
      },
    });
  }

  /**
   * Включение проигрывания видео определенного потока.
   * Возможная проблема https://developers.google.com/web/updates/2016/03/play-returns-promise
   *
   * @param {Number} streamNumber
   */
  play(streamNumber) {
    this.instancePlayer.play(`v${streamNumber}`, "a1");
  }

  /**
   * Приостановка проигрывания видео.
   */
  pause() {
    this.instancePlayer.pause();
  }

  /**
   * Выключение плеера.
   *
   * @return {Promise}
   */
  switchOff() {
    return this.instancePlayer.stop();
  }

  /**
   * Установка номера потока для воспроизведения.
   * Вызывать метод необходимо непосредственно в процессе воспроизведения.
   *
   * @param {Number} streamNumber
   */
  setTrack(streamNumber) {
    return this.instancePlayer.setTracks([`v${streamNumber}`, "a1"]);
  }
}
export class PlayerMSEManul {
  /**
   * @param {HTMLVideoElement|Element} elementPlayer
   * @param {String} sourceUrl
   */
  constructor(elementPlayer, sourceUrl) {
    this.elementPlayer = elementPlayer;
    this.instancePlayer = new VideoPlayerWithWebSocket(elementPlayer, sourceUrl);
  }
  /**
   * Включение проигрывания видео определенного потока.
   *
   */
  play() {
    this.instancePlayer.play()
  }
  /**
   * Приостановка проигрывания видео.
   */
  pause() {
    this.instancePlayer.pause();
  }
  /**
   * Выключение плеера.
   *
   * @return {Promise}
   */
  switchOff() {
    return Promise.resolve().then(() => this.instancePlayer.destroy());
  }
}

/**
 * Абстракция поверх hls.js.
 */
export class PlayerHLS {
  /**
   * @param {HTMLVideoElement|Element} elementPlayer
   * @param {String} sourceUrl
   * @param {Function} onError
   * @param {Boolean} archiveMode
   */
  constructor(elementPlayer, sourceUrl, onError, archiveMode) {
    this.elementPlayer = elementPlayer;
    this.instancePlayer = null; // Инициализируем переменную instancePlayer как null.

    // Проверяем поддержку HLS перед инициализацией Hls.js.
    if (Hls.isSupported()) {
      // 0. Инициализация контекста Hls.js - при переключении между источниками видео необходимо последовательно разрушать
      // и заново создавать, в том числе чтобы избежать повторного навешивания обработчиков.
      this.instancePlayer = new Hls({
        maxMaxBufferLength: 120, // 2 мин. на сохранение видео для воспроизведения (в тч вперед-назад).
        backBufferLength: 120, // 2 мин. на сохранение проигранного куска видео.
        minMaxBufferLength: 10,
        // liveDurationInfinity: true, // todo может быть полезно если надо четко определить duration как Inf как это в случае с MSE fluss
        manifestLoadingTimeOut: 15000,
        levelLoadingTimeOut: 15000,
        // В случае если воспроизводится архив HLS Event playlist и ссылка имеет вид http://FLUSSONIC-IP/STREAMNAME/index-1362504585-now.m3u8
        // то (по умолчанию) при переходе из предыдущего фрагмента в следующий hls.js начинает показ видео с последнего фрагмента из плейлиста.
        // В нашем плеере нужно сделать плавный переход из одного фрагмента к другому,
        // чтобы после окончания первого воспроизведение продолжалось с начала следующего.
        // Поэтому явно установлена начальная позиция воспроизведения.
        startPosition: archiveMode ? 0 : -1,
      });

      // Продолжаем только если Hls.js успешно инициализирован.
      if (this.instancePlayer) {
        // 1. Установка связи между html5-плеером и Hls.js для организации работы с потоковым видео.
        this.instancePlayer.attachMedia(elementPlayer);
        // 2. Перехват события, которое срабатывает после установки связи, для установки актуального источника.
        this.instancePlayer.on(Hls.Events.MEDIA_ATTACHED, () => {
          this.instancePlayer.loadSource(sourceUrl);
        });
        this.instancePlayer.on(Hls.Events.MANIFEST_PARSED, () => {
          this.elementPlayer.play();
        });
        this.instancePlayer.on(Hls.Events.ERROR, (event, data) => {
          devLog("[HLS error]", event, data);

          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                this.instancePlayer.startLoad();
                if (data.details === "manifestParsingError") {
                  onError();
                }
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                if (data.details === "fragParsingError") {
                  onError();
                }
                break;
              default:
                this.instancePlayer.destroy();
                onError();
                break;
            }
          } else {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                if (data.details === "levelLoadError") {
                  onError();
                }
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                // bufferStalledError происходит когда медленный интернет или клиентское устройство и не может своевременно обработать новые фрагменты.
                // Если видео хлс и перейти на другую вкладку (оставить окно неактивным) минимум на 20 сек - будет ошибка "bufferStalledError".
                // this.instancePlayer.recoverMediaError(); // Ранее было достаточно сделать рекавери.
                this.instancePlayer.recoverMediaError();
                onError();
                break;
              default:
                break;
            }
          }
        });
      }
    } else {
      // Если HLS не поддерживается, используем обычный HTML5 плеер.
      elementPlayer.src = sourceUrl;
      elementPlayer.addEventListener('loadedmetadata', function() {
      });
    }
  }

  /**
   * Включение проигрывания видео определенного потока.
   *
   * @param {Number} streamNumber
   */
  // eslint-disable-next-line no-unused-vars
  play(streamNumber) {
    this.elementPlayer.play();
  }

  /**
   * Приостановка проигрывания видео.
   */
  pause() {
    if (this.elementPlayer) {
      this.elementPlayer.pause();
    }
  }

  /**
   * Выключение плеера.
   *
   * @return {Promise}
   */
  switchOff() {
    return Promise.resolve().then(() => {
      if (this.instancePlayer) {
        this.instancePlayer.destroy();
      }
    });
  }
}
/**
 * Опции для выбора зоны для редактирования.
 */
export const ZONES_OPTIONS = Object.freeze({
  0: {id: 0, color: "#52C133", title: "Зеленая"},
  1: {id: 1, color: "#ff6b6b", title: "Красная"},
  2: {id: 2, color: "#0093E9", title: "Синяя"},
  3: {id: 3, color: "#6e48aa", title: "Фиолетовая"},
});

/**
 * Класс для представления информации о зонах аналитики.
 *
 * Точки - массив объектов вида {x: Number, y: Number}.
 */
export class AnalyticZone {
  constructor(points = [], sensitivity = 1) {
    this.points = points;
    this.sensitivity = sensitivity;
  }

  /**
   * Вернет массив координат точек в представлении целых чисел.
   * В процессе перемещения точек координаты могут быть представлены не целыми числами.
   * Поэтому для экспорта используется отдельный метод с отбрасыванием части после запятой.
   *
   * @return {[{x: Number, y: Number}]}
   */
  exportPoints() {
    return this.points.map((point) => ({x: Math.trunc(point.x), y: Math.trunc(point.y)}));
  }
}

/**
 * Настройки для зон аналитики.
 * Так как в рамках аналитики может быть несколько зон со своими собственными настройками, для них всех
 * так же может существовать общий набор настроек.
 */
export class AnalyticZonesConfig {
  /**
   * @param {Number} zonesWidth Ширина зон, в пределах которых существуют точки на зонах.
   * @param {Number} zonesHeight Высота зон, в пределах которых существуют точки на зонах.
   * @param {Number} minPointsCount Минимальное количество точек в зоне для экспорта.
   * @param {Number} maxPointsCount Максимальное количество точек в зоне для экспорта и редактирования.
   * @param {Array<AnalyticZone>} zones Массив с объектами зон.
   */
  constructor(zonesWidth = 0, zonesHeight = 0, minPointsCount = 3, maxPointsCount = 100, zones = [], motionAlarm = false) {
    this.zonesWidth = zonesWidth;
    this.zonesHeight = zonesHeight;
    this.minPointsCount = minPointsCount;
    this.maxPointsCount = maxPointsCount;
    this.zones = zones;
    this.motionAlarm = motionAlarm;
  }

  /**
   * Вернет массив зон, которые удовлетворяют условию количество точек.
   *
   * @return {Array<AnalyticZone>}
   */
  exportZones() {
    return this.zones.map((zone) => {
      if (zone.points.length >= this.minPointsCount && zone.points.length <= this.maxPointsCount) {
        return zone;
      }
    })
      .filter((zone) => zone);
  }
}

export class AnalyticLine {
  constructor(points = []) {
    this.points = points;
  }
}

/**
 * Настройки для зон аналитики.
 * Так как в рамках аналитики может быть несколько зон со своими собственными настройками, для них всех
 * так же может существовать общий набор настроек.
 */
export class AnalyticLinesConfig {
  /**
   * @param {Number} linesWidth Ширина зон, в пределах которых существуют точки на зонах.
   * @param {Number} linesHeight Высота зон, в пределах которых существуют точки на зонах.
   * @param {Number} minPointsCount Минимальное количество точек в зоне для экспорта.
   * @param {Number} maxPointsCount Максимальное количество точек в зоне для экспорта и редактирования.
   * @param {Array<AnalyticLine>} lines Массив с объектами зон.
   */
  constructor(linesWidth = 0, linesHeight = 0, minPointsCount = 1, maxPointsCount = 3, lines = []) {
    this.linesWidth = linesWidth;
    this.linesHeight = linesHeight;
    this.minPointsCount = minPointsCount;
    this.maxPointsCount = maxPointsCount;
    this.lines = lines;
  }
}

/**
 * Шина сообщений для обмена информацией между плеером (-ми) и внешними средствами контроля.
 * Используется обычный vue экземпляр, т.к. все необходимые методы для pub-sub у него есть.
 */
const PLAYER_BUS = new Vue();

/**
 * Миксин с методами для работы с шиной событий. Необходимо примешивать его и в плееры и в медиатор.
 * Методы представлены парами: метод для отправки сообщения и пустой метод для обработки сообщения.
 *
 * Пустой метод должен быть перекрыт в компоненте, и содержать там конечную логику для работы компонента.
 * Метод для отправки события можно вызывать в компоненте без перекрытия - как он есть в миксине.
 * При отправке события, все подписчики получат его и вызовут подписанные методы, которые должны быть перекрыты.
 *
 * Подписка и отписка на события происходит в обычных хуках.
 * Все методы отправки события закачиваются постфиксом ViaBus.
 */
export const playerBusMixin = {
  /**
   * Подписки на события и привязка к событиям конкретных методов.
   */
  mounted() {
    PLAYER_BUS.$on("PLAY", this.play);
    PLAYER_BUS.$on("PAUSE", this.pause);
    PLAYER_BUS.$on("SET_PLAYBACK_RATE", this.setPlaybackRate);
    PLAYER_BUS.$on("SHIFT_CURRENT_TIME_BY_SECONDS", this.shiftCurrentTimeBySeconds);
    PLAYER_BUS.$on("UPDATE_CURRENT_TIME_BY_TIME_SHIFT", this.updateCurrentTimeByTimeShift);
    PLAYER_BUS.$on("GO_TO_LIVE", this.goToLive);
  },
  /**
   * Отписки от событий.
   */
  beforeDestroy() {
    PLAYER_BUS.$off("PLAY", this.play);
    PLAYER_BUS.$off("PAUSE", this.pause);
    PLAYER_BUS.$off("SET_PLAYBACK_RATE", this.setPlaybackRate);
    PLAYER_BUS.$off("SHIFT_CURRENT_TIME_BY_SECONDS", this.shiftCurrentTimeBySeconds);
    PLAYER_BUS.$off("UPDATE_CURRENT_TIME_BY_TIME_SHIFT", this.updateCurrentTimeByTimeShift);
    PLAYER_BUS.$off("GO_TO_LIVE", this.goToLive);
  },
  methods: {
    /**
     * Включение проигрывания видео актуального потока.
     */
    playViaBus() {
      PLAYER_BUS.$emit("PLAY");
    },
    /**
     * @see playViaBus
     */
    play() {
    },
    /**
     * Приостановка проигрывания видео.
     */
    pauseViaBus() {
      PLAYER_BUS.$emit("PAUSE");
    },
    /**
     * @see pauseViaBus
     */
    pause() {
    },
    /**
     * Установит локальное значение скорости воспроизведения.
     */
    setPlaybackRateViaBus(playbackRate) {
      PLAYER_BUS.$emit("SET_PLAYBACK_RATE", playbackRate);
    },
    /**
     * @see setPlaybackRateViaBus
     */
    setPlaybackRate() {
    },
    /**
     * Сдвинет время в плеере на указанное количество секунд от текущей позиции timeShift.
     *
     * @param {Number} secondsForShift положительное или отрицательное количество секунд, с которым будет суммироваться время сдвига.
     */
    shiftCurrentTimeBySecondsViaBus(secondsForShift) {
      PLAYER_BUS.$emit("SHIFT_CURRENT_TIME_BY_SECONDS", secondsForShift);
    },
    /**
     * @see shiftCurrentTimeBySecondsViaBus
     */
    shiftCurrentTimeBySeconds() {
    },
    /**
     * Обновление текущего времени воспроизведения элемента video.
     *
     * @param {Date} newTimeShift
     */
    updateCurrentTimeByTimeShiftViaBus(newTimeShift) {
      PLAYER_BUS.$emit("UPDATE_CURRENT_TIME_BY_TIME_SHIFT", newTimeShift);
    },
    /**
     * @see updateCurrentTimeByTimeShiftViaBus
     */
    updateCurrentTimeByTimeShift() {
    },
    /**
     * Простой перевод плеера из архивного режима в режим воспроизведения прямой трансляции.
     */
    goToLiveViaBus() {
      PLAYER_BUS.$emit("GO_TO_LIVE");
    },
    /**
     * @see goToLiveViaBus
     */
    goToLive() {
    },
  },
};
